import type { AnalysisConfig, AnalysisStepConfig, QuizDefinition } from "@quiz/quiz-core";
import { getAnalysisVisual, getQuizPresentation } from "@quiz/quiz-definitions";
import { Check, createElement, FileText, MessageCircle, ScanText } from "lucide";
import { el } from "../utils";
import { renderCampaignMedia } from "../components/CampaignMedia";

export function renderAnalysisScreen(quiz: QuizDefinition, onDone: () => void): HTMLElement {
  const screen = el("main", "screen analysis-screen");
  const comic = getQuizPresentation(quiz).comicQuest;
  if (comic?.analysisBackdrop) screen.style.setProperty("--analysis-backdrop", `url("${comic.analysisBackdrop.src}")`);
  const shell = el("section", "analysis-shell");
  const compact = getQuizPresentation(quiz).flowLayout === "compact";
  const ukrainian = quiz.metadata.language.toLowerCase().startsWith("uk");
  const labels = ukrainian
    ? { eyebrow: "Твій маршрут навчання", pending: "Попереду", active: "Опрацьовуємо", done: "Готово", complete: "Твоя рекомендація готова" }
    : { eyebrow: "Your learning journey", pending: "Up next", active: "In progress", done: "Complete", complete: "Your recommendation is ready" };
  if (!compact) shell.append(el("p", "eyebrow", labels.eyebrow));
  const title = el("h1", "analysis-title", quiz.analysis.title ?? "Готуємо результат");
  const header = el("div", "analysis-heading");
  const visual = getAnalysisVisual(quiz);
  const media = renderCampaignMedia(visual, "analysis-media");
  if (media && visual) {
    header.classList.add(`analysis-heading--${visual.placement}`);
    media.dataset.shape = visual.shape ?? "rounded";
    media.style.setProperty("--analysis-media-ratio", String(visual.width / visual.height));
    header.append(media);
  }
  header.append(title);
  shell.append(header);
  if (quiz.analysis.subtitle) shell.append(el("p", "analysis-subtitle", quiz.analysis.subtitle));

  const stages = normalizeSteps(quiz.analysis).slice(0, 3);
  const grid = el("div", "analysis-stage-grid");
  const cards = stages.map((stage, index) => {
    const card = el("article", "analysis-stage-card");
    const badge = el("span", "analysis-stage-card__index", `0${index + 1}`);
    if (compact || comic) {
      badge.replaceChildren(createElement([ScanText, MessageCircle, FileText][index], { width: 19, height: 19 }));
    }
    badge.setAttribute("aria-hidden", "true");
    const status = el("span", "analysis-stage-card__status", labels.pending);
    const heading = el("div", "analysis-stage-card__heading");
    heading.append(badge, status);
    card.append(heading);
    card.append(el("h2", "analysis-stage-card__title", stage.title));
    if (stage.description) card.append(el("p", "analysis-stage-card__text", stage.description));
    const meter = el("div", "analysis-stage-meter");
    meter.setAttribute("role", "progressbar");
    meter.setAttribute("aria-label", stage.title);
    meter.setAttribute("aria-valuemin", "0");
    meter.setAttribute("aria-valuemax", "100");
    meter.setAttribute("aria-valuenow", "0");
    const bar = el("div", "analysis-stage-meter__bar");
    const value = el("strong", "analysis-stage-meter__value", "0%");
    meter.append(bar);
    card.append(meter, value);
    grid.append(card);
    return { card, bar, value, meter, badge, status };
  });
  shell.append(grid);
  const summary = el("p", "analysis-summary");
  summary.setAttribute("role", "status");
  shell.append(summary);
  screen.append(shell);

  void animateStageCards(quiz.analysis, cards, labels, summary, screen).then((completed) => {
    if (completed) onDone();
  });
  return screen;
}

function normalizeSteps(config: AnalysisConfig): AnalysisStepConfig[] {
  return config.steps.map((step, index) =>
    typeof step === "string"
      ? { id: `step-${index + 1}`, title: step }
      : step
  );
}

async function animateStageCards(
  config: AnalysisConfig,
  cards: Array<{ card: HTMLElement; bar: HTMLElement; value: HTMLElement; meter: HTMLElement; badge: HTMLElement; status: HTMLElement }>,
  labels: { active: string; done: string; complete: string },
  summary: HTMLElement,
  screen: HTMLElement
): Promise<boolean> {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const duration = prefersReducedMotion ? config.reducedMotionDurationMs ?? 300 : config.durationMs;
  const rhythms = [
    [0, 18, 34, 43, 43, 61, 79, 91, 91, 100],
    [0, 12, 29, 51, 51, 68, 84, 94, 94, 100],
    [0, 21, 38, 38, 57, 76, 88, 96, 96, 100]
  ];

  for (const [index, card] of cards.entries()) {
    const checkpoints = prefersReducedMotion ? [100] : rhythms[index % rhythms.length];
    const interval = duration / Math.max(1, cards.length * checkpoints.length);
    card.card.classList.add("is-active");
    card.status.textContent = labels.active;
    for (const value of checkpoints) {
      card.bar.style.width = `${value}%`;
      card.value.textContent = `${value}%`;
      card.meter.setAttribute("aria-valuenow", String(value));
      await delay(interval);
      if (!screen.isConnected) return false;
    }
    card.card.classList.remove("is-active");
    card.card.classList.add("is-complete");
    if (card.badge.querySelector("svg")) {
      card.badge.replaceChildren(createElement(Check, { width: 19, height: 19 }));
    } else card.badge.textContent = "✓";
    card.status.textContent = labels.done;
  }
  summary.textContent = labels.complete;
  screen.classList.add("is-complete");
  await delay(prefersReducedMotion ? 100 : 400);
  return screen.isConnected;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
